import { AddCommentModal } from "@/components";
import { CommentTarget, PROJECT_STATUS } from "@/enums";
import { getProjectStatusTranslationId } from "@/helpers";
import { useMobileLayout } from "@/hooks/useAppMedia";
import { useProjectContext } from "@/providers";
import { useChangeStatusMutation } from "@/services";
import * as S from "@/sharedStyled";
import { Alert, Radio, RadioChangeEvent, Space, Spin } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getCanChangeToCompleteFromActive } from "../../helpers/getCanChangeToCompleteFromActive";

interface IProps {
  isOpen: boolean;
  onCancel: () => void;
  availableStatuses: { id: string; name: string }[];
  loadingStatuses: boolean;
}

export const ChangeStatusModal = ({
  isOpen,
  onCancel,
  availableStatuses,
  loadingStatuses,
}: IProps) => {
  const intl = useIntl();
  const { projectId } = useParams();
  const isMobileLayout = useMobileLayout();
  const [form, setForm] = useState<{ id: string; name: string } | null>(null);
  const { setProject, project } = useProjectContext();

  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentTag, setCommentTag] = useState<string>("");

  const [changeStatus, { isLoading: statusChangeLoading }] =
    useChangeStatusMutation();

  const changeStatusRequiresComment = (status: string): boolean => {
    switch (status) {
      case PROJECT_STATUS.intake.canceled.id:
      case PROJECT_STATUS.project.canceled.id:
        setCommentTag("canceled");
        return true;
      case PROJECT_STATUS.intake.onHold.id:
      case PROJECT_STATUS.project.onHold.id:
        setCommentTag("on hold");
        return true;
      case PROJECT_STATUS.project.active.id:
      case PROJECT_STATUS.intake.draft.id:
        setCommentTag("resume");
        return true;
      case PROJECT_STATUS.project.complete.id:
        setCommentTag("completed");
        return true;
      default:
        return false;
    }
  };

  const handleOnFinish = (): void => {
    const requireComment = changeStatusRequiresComment(form?.id || "");
    setShowCommentModal(requireComment);

    if (!requireComment) changeProjectStatus();
  };

  const changeProjectStatus = (callback?: () => void): void => {
    if (!form?.id) {
      onCancel();
      return;
    }

    changeStatus({
      projectId,
      statusId: form.id,
    })
      .unwrap()
      .then((data) => {
        if (callback) callback();
        setProject(data);
        onCancel();
        toast.success(intl.formatMessage({ id: "changeStatusModal.saved" }));
      })
      .catch((error) => {
        onCancel();
        console.error(error);
        toast.error(intl.formatMessage({ id: "changeStatusModal.error" }));
      });
  };

  const [canCompleteFromActive, setCanCompleteFromActive] = useState(false);

  const onChange = (e: RadioChangeEvent): void => {
    setForm(e.target.value);
  };

  useEffect(() => {
    setCanCompleteFromActive(getCanChangeToCompleteFromActive(project));
  }, [availableStatuses, project]);

  return (
    <>
      <S.ResponsiveModal
        $mobile={isMobileLayout}
        maskClosable={false}
        title={intl.formatMessage({
          id: "changeStatusModal.title",
        })}
        onOk={handleOnFinish}
        onCancel={onCancel}
        open={isOpen && !!availableStatuses?.length}
        okText={intl.formatMessage({
          id: "changeStatusModal.change",
        })}
        confirmLoading={statusChangeLoading}
        centered={true}
      >
        {loadingStatuses || !project ? (
          <Spin />
        ) : (
          <>
            {!canCompleteFromActive && (
              <Alert
                className="mb-1"
                message={intl.formatMessage({
                  id: "changeStatusModal.cannotComplete",
                })}
                type="error"
              />
            )}

            {form?.id === PROJECT_STATUS.project.canceled.id && (
              <Alert
                className="mb-1"
                message={intl.formatMessage({
                  id: "changeStatusModal.projectCanceledWarning",
                })}
                type="warning"
              />
            )}

            {form?.id === PROJECT_STATUS.intake.canceled.id && (
              <Alert
                className="mb-1"
                message={intl.formatMessage({
                  id: "changeStatusModal.intakeCanceledWarning",
                })}
                type="warning"
              />
            )}

            <Radio.Group data-test-id="" onChange={onChange} value={form}>
              <Space direction="vertical">
                {availableStatuses?.map((status) => (
                  <Radio key={status.id} value={status}>
                    {intl.formatMessage({
                      id: getProjectStatusTranslationId(status.id),
                    })}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </>
        )}
      </S.ResponsiveModal>
      <AddCommentModal
        isVisible={showCommentModal}
        target={CommentTarget.IntakeProject}
        targetId={projectId}
        projectId={projectId ?? ""}
        setVisible={setShowCommentModal}
        tagName={commentTag}
        tagDisabled={true}
        onFinish={(callback: () => void): void => changeProjectStatus(callback)}
      />
    </>
  );
};
